import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, doc, onSnapshot, query, where } from 'firebase/firestore';
import { AlertTriangle, Plus, Search, Users } from 'lucide-react';
import { db } from '@/lib/firebase';
import { useUnitContext } from '@/contexts/UnitContext';
import { listTreasurerRoster, type TreasurerRosterRow } from '@/lib/treasurerApi';

const STATUS_BADGE: Record<string, { bg: string; text: string; label: string }> = {
  active: { bg: 'bg-emerald-50', text: 'text-emerald-700', label: 'Active' },
  honorary: { bg: 'bg-blue-50', text: 'text-blue-700', label: 'Honorary' },
  joining: { bg: 'bg-amber-50', text: 'text-amber-700', label: 'Joining' },
  resigned: { bg: 'bg-slate-100', text: 'text-slate-600', label: 'Resigned' },
  deceased: { bg: 'bg-slate-100', text: 'text-slate-600', label: 'Deceased' },
  suspended: { bg: 'bg-red-50', text: 'text-red-700', label: 'Suspended' },
};

function formatPence(pence: number): string {
  const negative = pence < 0;
  const abs = Math.abs(Math.trunc(pence));
  const pounds = Math.floor(abs / 100);
  const remainder = abs % 100;
  return `${negative ? '-' : ''}£${pounds.toLocaleString('en-GB')}.${remainder.toString().padStart(2, '0')}`;
}

function rowKey(r: TreasurerRosterRow): string {
  return r.treasurerMemberId ?? (r.linkedUserId ? `uid:${r.linkedUserId}` : `anon:${r.firstName}:${r.lastName}`);
}

export default function MembersListPage() {
  const { activeUnit, isTreasurer } = useUnitContext();
  const [members, setMembers] = useState<TreasurerRosterRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    if (!activeUnit) return;

    const runLoad = () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(async () => {
        try {
          const { members: rows } = await listTreasurerRoster({ unitId: activeUnit.unitId });
          setMembers(rows);
        } catch (err) {
          console.error('Failed to load roster', err);
        } finally {
          setLoading(false);
        }
      }, 200);
    };

    setLoading(true);
    runLoad();

    const unsubUnit = onSnapshot(doc(db, 'units', activeUnit.unitId), runLoad);
    const tmQuery = query(
      collection(db, 'units', activeUnit.unitId, 'treasurerMembers'),
      where('isArchived', '==', false),
    );
    const unsubTm = onSnapshot(tmQuery, runLoad);

    return () => {
      unsubUnit();
      unsubTm();
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [activeUnit]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return members.filter((m) => {
      if (statusFilter !== 'all' && m.status !== statusFilter) return false;
      if (!term) return true;
      return (
        m.firstName.toLowerCase().includes(term) ||
        m.lastName.toLowerCase().includes(term) ||
        (m.email ?? '').toLowerCase().includes(term) ||
        (m.membershipNumber ?? '').toLowerCase().includes(term) ||
        m.paymentReference.toLowerCase().includes(term)
      );
    });
  }, [members, search, statusFilter]);

  if (!activeUnit) return null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Users className="h-6 w-6 text-emerald-600" /> Members
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Lodge membership from the unit roster — subscription and payment details from treasurer records.
          </p>
          <p className="text-slate-400 text-xs mt-0.5">
            {members.length} {members.length === 1 ? 'person' : 'people'}
          </p>
        </div>
        {isTreasurer && (
          <Link
            to={`/u/${activeUnit.unitId}/members/new`}
            className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-md font-medium hover:bg-emerald-700"
          >
            <Plus className="h-4 w-4" /> Add manual member
          </Link>
        )}
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="search"
            placeholder="Search name, email, membership number or reference"
            className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:border-emerald-500"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-slate-300 rounded-md text-sm bg-white"
        >
          <option value="all">All statuses</option>
          {Object.keys(STATUS_BADGE).map((s) => (
            <option key={s} value={s}>{STATUS_BADGE[s].label}</option>
          ))}
        </select>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-500 text-sm">Loading members…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-slate-500 text-sm">
            {members.length === 0 ? 'No lodge members on this unit yet.' : 'No matching members.'}
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-slate-50 text-xs font-medium text-slate-600 uppercase">
              <tr>
                <th className="px-4 py-3 text-left">Name</th>
                <th className="px-4 py-3 text-left">Membership №</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-right">Annual sub</th>
                <th className="px-4 py-3 text-left">Reference</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((m) => {
                const badge = STATUS_BADGE[m.status] ?? STATUS_BADGE.active;
                const rosterState =
                  m.needsTreasurerRecord && m.linkedUserId
                    ? {
                        fromRoster: {
                          linkedUserId: m.linkedUserId,
                          firstName: m.firstName,
                          lastName: m.lastName,
                          email: m.email,
                        },
                      }
                    : undefined;
                const toPath =
                  m.treasurerMemberId && !m.needsTreasurerRecord
                    ? `/u/${activeUnit.unitId}/members/${m.treasurerMemberId}`
                    : `/u/${activeUnit.unitId}/members/new`;

                return (
                  <tr key={rowKey(m)} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <Link
                        to={toPath}
                        state={rosterState}
                        className="text-emerald-700 hover:underline font-medium"
                      >
                        {m.firstName} {m.lastName}
                      </Link>
                      {m.email && <div className="text-xs text-slate-500 mt-0.5">{m.email}</div>}
                      {m.needsTreasurerRecord && (
                        <div className="text-xs text-amber-700 mt-0.5">Treasurer record not set up — click to add subscription details</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">{m.membershipNumber || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center text-xs font-medium px-2 py-0.5 rounded ${badge.bg} ${badge.text}`}>
                        {badge.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-sm tabular-nums">
                      {m.needsTreasurerRecord ? '—' : formatPence(m.annualSubscriptionPence)}
                    </td>
                    <td className="px-4 py-3 text-sm font-mono text-slate-700">
                      <span className="flex items-center gap-2">
                        {m.paymentReference || (m.needsTreasurerRecord ? '—' : <span className="text-slate-400">none</span>)}
                        {!m.needsTreasurerRecord && m.hasReferenceCollision && (
                          <span title="Another active member has the same payment reference">
                            <AlertTriangle className="h-4 w-4 text-amber-500" />
                          </span>
                        )}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
